import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { hasPermission } from "../../utils/permission";
import Card from "@mui/material/Card";
import List from "@mui/material/List";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { EmptyState, RowButton, StatusChip } from "../../components/mui";

/**
 * 搜尋（設計稿 el-pm-template_4.html:2092-2129 #screen-search）
 *
 * 舊系統只有 ⌘K 那個彈窗，而且提示寫的是「⌘K」—— 公司大半是 Windows，
 * 那個符號看不懂也按不出來。所以這一頁做兩件事：
 *   1. 給搜尋一個真的網址（頁首的「搜尋」鈕、側欄入口都導到這裡）；
 *   2. 把快捷鍵用文字寫出來：「Ctrl + K（Mac 是 ⌘ + K）」。
 *
 * ⌘K 的彈窗（layouts/parts/CommandPalette.js）完全保留，Ctrl + K 仍然開它 ——
 * 習慣鍵盤的人不必改用法，這一頁是給用滑鼠的人走的另一條路。
 *
 * 版面規則：不自己包頁面內距（Layout 已經給了），整頁沒有 contained 主要按鈕 ——
 * 搜尋沒有「主要動作」，打字就會出結果。
 */

/** 清單端點有時回純陣列、有時回 { data_list, total_count } */
const toArray = (v) => {
  if (Array.isArray(v)) return v;
  if (Array.isArray(_.get(v, "data_list"))) return v.data_list;
  if (Array.isArray(_.get(v, "list"))) return v.list;
  if (Array.isArray(_.get(v, "items"))) return v.items;
  return [];
};

/**
 * 一次列出的上限。
 *
 * 打「工」這種一個字的關鍵字會命中幾百筆，全部畫出來只是讓人往下捲到放棄。
 * 超過就在下面寫清楚還有幾筆、請再打幾個字 —— 不做分頁器：搜尋的下一步是
 * 「把關鍵字打長一點」，不是「翻到第 7 頁」。
 */
const MAX_ROWS = 20;

function SearchIndex(props) {
  const navigate = useNavigate();
  const { GET_ProjectList, GET_MyTaskList, GET_AdminList } = props;
  const [q, setQ] = useState("");

  /*
   * 人員清單走 /Auth/Admin/List，那支端點掛的是 admin 模組權限。
   * 沒有權限的人打過去會被擋下來、跳一則紅字，所以連請求都不要發，
   * 「人」這個型別對他們就不存在（後端仍然是真正的把關，這裡只是不去撞牆）。
   */
  const canSearchPeople = useMemo(() => hasPermission("admin"), []);

  useEffect(() => {
    GET_ProjectList({ Page: 0, PageCount: 0 }, null, null);
    GET_MyTaskList(null, null, null);
    if (canSearchPeople) GET_AdminList(null, null);
  }, [GET_ProjectList, GET_MyTaskList, GET_AdminList, canSearchPeople]);

  /*
   * 可搜尋的東西攤平成同一種形狀，種類用類型標籤（StatusChip kind="type"）寫在標題前面。
   *
   * 「文件」這個型別暫時不做：後端的文件清單是 /Document/List?projectId=…，
   * 一次只能問一個專案，沒有跨專案的文件搜尋端點。硬做就要對每個專案各打一次，
   * 專案一多就是幾十個請求 —— 等後端補上全域搜尋再接（設計稿 L2118 有這一列）。
   */
  const items = useMemo(() => {
    const rows = [];

    toArray(props.projects).forEach((p) => {
      if (!p || !p.name) return;
      rows.push({
        key: `p-${p.id}`,
        type: "專案",
        title: p.name,
        metas: [p.code, p.client_name].filter(Boolean),
        to: p.id != null ? `/Project/Detail/${p.id}` : "",
      });
    });

    toArray(props.myTasks).forEach((t) => {
      if (!t || !t.title) return;
      rows.push({
        key: `t-${t.id}`,
        type: "工項",
        /* 負責人那一欄設計稿有、/MyTask/List 沒有回（見 MyTaskService 的 Select），
           所以只放專案名。寧可少一格，也不要湊一個假的人名上去 */
        title: t.title,
        metas: [t.project_name].filter(Boolean),
        to: t.project_id != null ? `/Project/Detail/${t.project_id}?task=${t.id}` : "",
      });
    });

    if (canSearchPeople) {
      toArray(props.admins).forEach((a) => {
        if (!a || !a.name) return;
        rows.push({
          key: `a-${a.account}`,
          type: "人員",
          title: a.name,
          metas: [a.account, a.email, _.get(a, "role.name")].filter(Boolean),
          /* 系統裡沒有「單一人員」的頁面，最接近的落點是使用者管理那一頁；
             會走到這裡的本來就是有 admin 權限的人（見 canSearchPeople） */
          to: "/Setting/Admin",
        });
      });
    }

    return rows;
  }, [props.projects, props.myTasks, props.admins, canSearchPeople]);

  const cq = q.trim().toLowerCase();
  const matched = useMemo(() => {
    if (!cq) return [];
    return items.filter((i) => `${i.title} ${i.metas.join(" ")}`.toLowerCase().includes(cq));
  }, [cq, items]);

  const shown = matched.slice(0, MAX_ROWS);

  return (
    <>
      <Typography variant="h1" component="h1">
        搜尋
      </Typography>
      <Typography sx={{ mt: "var(--space-3)" }}>在任何畫面按下 Ctrl + K（Mac 為 ⌘ + K）亦可開啟搜尋。</Typography>

      {/* max-width 照設計稿 L2096：輸入框拉滿反而難瞄準，
          而且眼睛要從左邊的游標一路跑到右邊才看得到自己打了什麼 */}
      <Stack spacing={1} sx={{ mt: "var(--space-6)", maxWidth: 640 }}>
        <Typography component="label" htmlFor="search-q" sx={{ fontWeight: 600 }}>
          搜尋專案、工項或人員
        </Typography>
        {/* 搜尋範圍的說明放在輸入框下方（全站一致：標籤 → 輸入框 → 說明） */}
        {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
        <TextField autoFocus
          placeholder="輸入專案名稱、代號、工項或人名"
          fullWidth
          id="search-q"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          helperText={
            canSearchPeople
              ? "搜尋範圍：專案名稱與代號、您負責的工項，以及系統人員。"
              : "搜尋範圍：專案名稱與代號，以及您負責的工項。"
          }
        />
      </Stack>

      {/* 還沒打字就什麼都不畫（硬規則 2）：一塊寫著「請輸入關鍵字」的空卡沒有用 */}
      {cq && shown.length === 0 ? (
        <EmptyState
          sx={{ mt: "var(--space-6)" }}
          title={<>找不到「{q.trim()}」</>}
          text={
            <>
              請嘗試僅輸入其中兩、三個字，或改用專案代號搜尋。搜尋範圍為您可檢視的專案與您負責的工項，
              不含其他人負責的工項。
            </>
          }
        />
      ) : null}

      {shown.length > 0 ? (
        <>
          <Typography variant="h3" component="p" sx={{ mt: "var(--space-6)" }}>
            共 {matched.length} 筆結果
          </Typography>
          <Card sx={{ mt: "var(--space-3)" }}>
            <List disablePadding>
              {shown.map((r) => (
                /* 整列是按鈕：可點範圍就是整列（遠大於 44px）而且帶文字（硬規則 3） */
                <RowButton
                  key={r.key}
                  type="button"
                  onClick={() => r.to && navigate(r.to)}
                  chevron={!!r.to}
                  title={
                    <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
                      <StatusChip kind="type">{r.type}</StatusChip>
                      <span>{r.title}</span>
                    </Stack>
                  }
                  meta={
                    r.metas.length > 0 ? (
                      <>
                        {r.metas.map((m) => (
                          <span key={m}>{m}</span>
                        ))}
                      </>
                    ) : null
                  }
                />
              ))}
            </List>
          </Card>
          {matched.length > shown.length ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: "var(--space-3)" }}>
              僅列出前 {shown.length} 筆，另有 {matched.length - shown.length} 筆。輸入更多關鍵字可縮小範圍。
            </Typography>
          ) : null}
        </>
      ) : null}
    </>
  );
}

SearchIndex.propTypes = {
  projects: PropTypes.any,
  myTasks: PropTypes.any,
  admins: PropTypes.any,
  GET_ProjectList: PropTypes.func,
  GET_MyTaskList: PropTypes.func,
  GET_AdminList: PropTypes.func,
};

SearchIndex.defaultProps = {
  projects: [],
  myTasks: [],
  admins: [],
  GET_ProjectList: () => {},
  GET_MyTaskList: () => {},
  GET_AdminList: () => {},
};

const mapStateToProps = (state) => ({
  projects: _.get(state, "Project.List", []),
  myTasks: _.get(state, "MyTask.List", []),
  admins: _.get(state, "Admin.List", []),
});

const mapDispatchToProps = (dispatch) => ({
  GET_ProjectList: (payload, callback, loading) =>
    dispatch({ type: "GET_ProjectList", payload, callback, loading }),
  GET_MyTaskList: (payload, callback, loading) =>
    dispatch({ type: "GET_MyTaskList", payload, callback, loading }),
  GET_AdminList: (callback, loading) => dispatch({ type: "GET_AdminList", callback, loading }),
});

export default connect(mapStateToProps, mapDispatchToProps)(SearchIndex);
